import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { presetsApi } from "../../api/presets";

const FOCUS_RING =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5500] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505]";

const ICON_SIZE = { sm: 14, md: 16 };

/**
 * FavoriteButton -- a toggle for a signed-in user's own private favourite on
 * one kit. Favouriting your own kit is allowed server-side (it's a private
 * bookmark, not a public signal like a rating), so unlike RatingStars there
 * is no 403/"can't favourite your own kit" case to special-case.
 *
 * Signed-out users see the exact same (non-favourited-looking) button --
 * never disabled/greyed-out, see rating-ui-report.md -- but activating it
 * routes to /login instead of calling the API.
 *
 * Props:
 * - presetId: string -- required.
 * - isFavorited: bool -- default false. The parent's current copy; this
 *   component keeps its own optimistic copy and only resyncs from this prop
 *   again if it (or `presetId`) actually changes.
 * - favoritesCount: number | undefined -- when provided, renders the
 *   bordered "pill" variant with the count next to the icon (matches the
 *   detail page's other h-9 controls, e.g. BpmControl's toggle buttons).
 *   Omit it for a bare icon-only toggle sized for a catalogue card corner.
 * - size: "sm" | "md" -- default "md".
 * - className: string -- merged onto the root button.
 * - onChange({ is_favorited, favorites_count }) -- called with the server's
 *   authoritative values after every successful toggle. Not called on
 *   failure (the optimistic flip is rolled back locally instead).
 *
 * A failed toggle rolls back and reports itself via a visually-hidden
 * (`sr-only`) live region plus a brief red ring on the button, rather than
 * flowing error text -- this button is meant to sit inside dense card
 * grids and a corner of a detail page alike, and reserving space for a
 * message (or shifting layout when one appears) doesn't hold up in either
 * spot. RatingStars, which only ever appears once per page in its
 * interactive form, can afford a visible message and needs one -- the
 * brief specifically wants the "can't rate your own kit" case surfaced
 * clearly there.
 */
export default function FavoriteButton({
  presetId,
  isFavorited = false,
  favoritesCount,
  size = "md",
  className = "",
  onChange,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favorited, setFavorited] = useState(isFavorited);
  const [count, setCount] = useState(favoritesCount);
  const [status, setStatus] = useState(null);
  const seqRef = useRef(0);
  const statusTimerRef = useRef(null);

  useEffect(() => {
    setFavorited(isFavorited);
    setCount(favoritesCount);
    setStatus(null);
  }, [presetId, isFavorited, favoritesCount]);

  useEffect(() => () => clearTimeout(statusTimerRef.current), []);

  const handleClick = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    const seq = ++seqRef.current;
    const prevFavorited = favorited;
    const prevCount = count;
    const nextFavorited = !favorited;
    setFavorited(nextFavorited);
    setCount((c) => (typeof c === "number" ? c + (nextFavorited ? 1 : -1) : c));
    setStatus(null);

    try {
      const { data } = nextFavorited
        ? await presetsApi.favorite(presetId)
        : await presetsApi.unfavorite(presetId);
      if (seq !== seqRef.current) return; // superseded by a later click
      setFavorited(data.is_favorited);
      if (typeof data.favorites_count === "number") setCount(data.favorites_count);
      onChange?.(data);
    } catch (err) {
      if (seq !== seqRef.current) return; // a newer click already moved on
      setFavorited(prevFavorited);
      setCount(prevCount);
      setStatus(err?.response?.data?.detail || "Couldn't update your favourites. Try again.");
      clearTimeout(statusTimerRef.current);
      statusTimerRef.current = setTimeout(() => setStatus(null), 5000);
    }
  };

  const label = !user
    ? "Sign in to add to favourites"
    : favorited
    ? "Remove from favourites"
    : "Add to favourites";

  const icon = ICON_SIZE[size] ?? ICON_SIZE.md;
  // Driven by the PROP, not by `count`. `count` starts undefined in icon-only
  // mode but the server always returns a number, so deriving this from state
  // made the first click permanently promote an icon-only button to the
  // bordered pill variant.
  const bordered = typeof favoritesCount === "number";

  const buttonClass = bordered
    ? `inline-flex h-9 items-center gap-1.5 rounded border px-2.5 font-mono text-xs transition-colors ${FOCUS_RING} ${
        favorited
          ? "border-[#FF5500] bg-[#FF5500]/10 text-[#FF5500]"
          : "border-white/10 bg-[#1a1a1e] text-[#A1A1AA] hover:border-white/25 hover:text-white"
      } ${status ? "ring-2 ring-red-500/60" : ""} ${className}`
    : `inline-flex items-center justify-center rounded p-1 transition-colors ${FOCUS_RING} ${
        favorited ? "text-[#FF5500]" : "text-[#A1A1AA] hover:text-white"
      } ${status ? "ring-2 ring-red-500/60" : ""} ${className}`;

  return (
    <>
      <button
        type="button"
        aria-pressed={!!user && favorited}
        aria-label={label}
        title={label}
        onClick={handleClick}
        className={buttonClass}
      >
        <Heart size={icon} strokeWidth={1.5} fill={favorited ? "currentColor" : "none"} />
        {bordered && <span>{count}</span>}
      </button>
      <span className="sr-only" role="status" aria-live="polite">
        {status}
      </span>
    </>
  );
}
